import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { useAuth } from "../context/AuthContext";
import { Building2, Flame, Wifi, Gauge, Droplets, Bell, AlertTriangle, ArrowUpRight, Activity, ShieldCheck, ClipboardList, CheckCircle2, ChevronRight, Users, SlidersHorizontal } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";

// Relative age for alert rows — "3m ago" reads faster than a bare time with no date.
function timeAgo(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return "";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return new Date(iso).toLocaleDateString([], { day: "numeric", month: "short" });
}

const CHART_TOOLTIP = { background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#1f2937", fontSize: 12 };

// Compact KPI tile — sized so the whole KPI strip fits on one row.
function KPI({ icon: Icon, label, value, subtitle, tone = "orange", testid, to }) {
  const gradients = {
    orange: "from-orange-500 to-pink-500",
    green: "from-emerald-400 to-emerald-600",
    red: "from-red-500 to-pink-600",
    blue: "from-sky-400 to-indigo-500",
    amber: "from-amber-400 to-orange-500",
    purple: "from-purple-500 to-indigo-500",
  };
  const inner = (
    <div className="glass relative px-2.5 py-2.5 rounded-lg border border-transparent hover:border-orange-300 transition-all group h-full flex items-center gap-2" data-testid={testid}>
      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${gradients[tone]} flex items-center justify-center shadow-sm shrink-0`}>
        <Icon className="w-4 h-4 text-white" strokeWidth={2.25} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xl font-extrabold tracking-tight text-gray-900 leading-none tabular-nums">{value}</div>
        <div className="text-[10px] uppercase tracking-wide text-gray-500 mt-1 leading-tight line-clamp-2">{label}</div>
        {subtitle && <div className="text-[10px] text-gray-400 truncate" title={subtitle}>{subtitle}</div>}
      </div>
      {to && <ArrowUpRight className="absolute top-1.5 right-1.5 w-3 h-3 text-gray-300 group-hover:text-orange-500 transition" strokeWidth={2.5} />}
    </div>
  );
  if (to) return <Link to={to} className="block">{inner}</Link>;
  return inner;
}

function SeverityDot({ severity }) {
  return (
    <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${severity === "critical" ? "bg-red-500 status-pulse-red" : severity === "warning" ? "bg-amber-500" : "bg-sky-500"}`} />
  );
}

function OnboardingChecklist({ s }) {
  const steps = [
    {
      id: "site",
      label: "Add your first site",
      desc: "Sites are the buildings or locations you monitor.",
      to: "/sites",
      done: (s?.total_sites || 0) > 0,
      icon: Building2,
    },
    {
      id: "device",
      label: "Register a device or gateway",
      desc: "Connect your IoT gateway or fire panel to start receiving live data.",
      to: "/devices",
      done: (s?.total_devices || 0) > 0,
      icon: Wifi,
    },
    {
      id: "alert",
      label: "Configure alert rules",
      desc: "Set thresholds so you get notified when something goes wrong.",
      to: "/alerts/settings",
      done: (s?.total_alert_rules || 0) > 0,
      icon: SlidersHorizontal,
    },
    {
      id: "team",
      label: "Invite your team",
      desc: "Add site managers, safety officers and viewers.",
      to: "/users",
      done: (s?.total_users || 0) > 1,
      icon: Users,
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null;
  const pct = Math.round((doneCount / steps.length) * 100);

  return (
    <div className="glass rounded-xl p-4 border-l-4 border-orange-500">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">Get started with Fire360Pro</h2>
          <p className="text-sm text-gray-500 mt-0.5">{doneCount} of {steps.length} steps complete</p>
        </div>
        <span className="text-2xl font-extrabold text-orange-500">{pct}%</span>
      </div>
      <div className="w-full h-1.5 bg-gray-200 rounded-full mb-3">
        <div className="h-1.5 f360-gradient rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
        {steps.map((step) => {
          const Icon = step.icon;
          return (
            <Link key={step.id} to={step.to}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${step.done
                ? "border-emerald-200 bg-emerald-50/50 opacity-60 cursor-default pointer-events-none"
                : "border-gray-200 hover:border-orange-300 hover:bg-orange-50/50 cursor-pointer"}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${step.done ? "bg-emerald-100" : "f360-gradient"}`}>
                {step.done
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  : <Icon className="w-5 h-5 text-white" strokeWidth={2} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-semibold text-sm ${step.done ? "line-through text-gray-400" : "text-gray-900"}`}>{step.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{step.desc}</div>
              </div>
              {!step.done && <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function AdminDashboard({ alerts, ack, criticalAlert }) {
  const [s, setS] = useState(null);
  const [assetStats, setAssetStats] = useState(null);
  const { sites: sitesList, selectedOrg, selectedSite, setSelectedSite } = useSiteContext();
  const { user: currentUser } = useAuth();
  const nav = useNavigate();
  const isPlatform = currentUser?.role === "super_admin" || currentUser?.role === "platform_support";

  const siteFilter = selectedSite && selectedSite !== "all" ? selectedSite : null;

  useEffect(() => {
    const params = {};
    if (selectedOrg) params.org_id = selectedOrg;
    if (siteFilter) params.site_id = siteFilter;
    const load = () => api.get("/stats/admin", { params }).then((r) => setS(r.data)).catch(() => {});
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
  }, [selectedOrg, siteFilter]);

  useEffect(() => {
    if (siteFilter) {
      api.get(`/assets/stats/${siteFilter}`).then((r) => setAssetStats(r.data)).catch(() => {});
    } else {
      api.get("/assets/stats", { params: isPlatform && selectedOrg ? { org_id: selectedOrg } : {} }).then((r) => setAssetStats(r.data)).catch(() => {});
    }
  }, [selectedOrg, siteFilter, isPlatform]);

  // Filter alerts to the selected site when one is pinned
  const visibleAlerts = siteFilter
    ? alerts?.filter((a) => a.site_id === siteFilter)
    : alerts;

  if (!s) return <div className="text-gray-500">Loading…</div>;

  const tone = (g, a, r) => r > 0 ? "red" : a > 0 ? "amber" : "green";
  const breakTankSites = sitesList.filter((st) => (st.break_tank_count || 0) > 0 || (st.total_height_m || 0) >= 90).length;
  const isNewOrg = s.total_sites === 0;

  const siteName = (id) => sitesList.find((st) => st.site_id === id)?.name;
  const openSite = (siteId) => {
    // Select the site, then open its Live Monitor — the site card used to go to the generic
    // /acms page regardless of which site was clicked.
    setSelectedSite(siteId);
    nav("/live-monitor");
  };
  const unackedCount = visibleAlerts?.filter((a) => !a.acknowledged).length || 0;

  return (
    <>
      {/* Header — one compact row */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-wrap">
          <h1 className="text-xl md:text-2xl font-extrabold tracking-tight truncate">
            {siteFilter ? (siteName(siteFilter) || "Site Dashboard") : "Admin Command Center"}
          </h1>
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold uppercase tracking-widest">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 status-pulse-green" /> Live
          </span>
          <span className="text-[11px] text-gray-500">
            {siteFilter ? "Single-site view" : "Multi-site fleet view"} · GR No. MFS-2023/C.R.370/UD-14
          </span>
        </div>
      </div>

      {!isPlatform && <OnboardingChecklist s={s} />}

      {isNewOrg ? (
        <div className="glass rounded-xl p-10 text-center text-gray-500 border border-dashed border-gray-200">
          <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <div className="font-semibold text-gray-700 mb-1">No sites yet</div>
          <p className="text-sm">Add your first site using the checklist above, then your live dashboard will appear here.</p>
        </div>
      ) : (
      <>
      {/* KPI strip — always one row of 7 on desktop; 2 / 4 per row on phones and tablets */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        <KPI testid="kpi-sites" icon={Building2} label="Total Sites" value={s.total_sites} tone="blue" to="/sites" />
        <KPI testid="kpi-devices" icon={Activity} label="Total Devices" value={s.total_devices} tone="purple" to="/devices" />
        <KPI testid="kpi-critical" icon={Flame} label="Open Critical" value={s.open_critical} tone={s.open_critical ? "red" : "green"} subtitle="Unacknowledged" to="/alerts" />
        <KPI testid="kpi-offline" icon={Wifi} label="Offline Devices" value={s.offline_devices} tone={s.offline_devices ? "red" : "green"} to="/devices" />
        <KPI testid="kpi-compliant" icon={ShieldCheck} label="Sites Compliant" value={`${s.compliance_pct}%`} tone={s.compliance_pct >= 80 ? "green" : s.compliance_pct >= 50 ? "amber" : "red"} subtitle="GR adherence" to="/reports" />
        <KPI testid="kpi-break-tank" icon={Droplets} label="Break Tank Sites" value={breakTankSites} tone="blue" subtitle="DCPR ≥90m" to="/sites" />
        <KPI testid="kpi-total-assets" icon={ClipboardList} label="Total Assets" value={assetStats?.total ?? "—"} tone="purple"
             subtitle={assetStats ? Object.entries(assetStats.by_type).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([k, v]) => `${v} ${k}`).join(" · ") : ""} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="glass p-4 rounded-lg lg:col-span-2" data-testid="alerts-timeline">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">Alerts — Last 30 Days</h3>
            <Link to="/alerts" className="text-[11px] text-orange-600 hover:underline">All alerts →</Link>
          </div>
          <ResponsiveContainer width="100%" height={230}>
            <LineChart data={s.alerts_timeline} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" stroke="#94a3b8" fontSize={10} tickLine={false} minTickGap={24} />
              <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={CHART_TOOLTIP} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="critical" stroke="#ef4444" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="warning" stroke="#f59e0b" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="info" stroke="#0ea5e9" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="glass p-4 rounded-lg flex flex-col" data-testid="recent-alerts">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">Recent Alerts</h3>
            {unackedCount > 0 && (
              <span className="px-1.5 py-px rounded-full bg-red-50 border border-red-200 text-red-700 text-[10px] font-bold">{unackedCount} unacknowledged</span>
            )}
          </div>
          <div className="divide-y divide-gray-100 overflow-y-auto max-h-[248px] -mx-1 px-1">
            {visibleAlerts?.slice(0, 8).map((a) => (
              <div key={a.alert_id} className="py-2 flex items-start gap-2.5">
                <SeverityDot severity={a.severity} />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm truncate ${a.acknowledged ? "text-gray-500" : "font-semibold text-gray-900"}`} title={a.title}>{a.title}</div>
                  <div className="text-[11px] text-gray-500 truncate">
                    {!siteFilter && siteName(a.site_id) ? `${siteName(a.site_id)} · ` : ""}{timeAgo(a.created_at)}
                  </div>
                </div>
                {!a.acknowledged ? (
                  <Button size="sm" variant="outline" onClick={() => ack(a.alert_id)} className="h-6 px-2 text-[10px] border-orange-200 text-orange-700 hover:bg-orange-50 shrink-0">Ack</Button>
                ) : (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-1 shrink-0" strokeWidth={2.5} />
                )}
              </div>
            ))}
            {!visibleAlerts?.length && <div className="text-sm text-gray-500 py-6 text-center">No alerts.</div>}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-bold">Site Health</h2>
          <span className="text-[11px] text-gray-500">Click a site to open its Live Monitor</span>
        </div>
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
          {s.site_health.map((site) => {
            const t = tone(site.green, site.amber, site.red);
            return (
              <button
                type="button"
                key={site.site_id}
                onClick={() => openSite(site.site_id)}
                className={`glass p-3 rounded-lg text-left border transition hover:shadow-md ${t === "red" ? "border-red-200 hover:border-red-300" : t === "amber" ? "border-amber-200 hover:border-amber-300" : "border-transparent hover:border-orange-300"}`}
                data-testid={`site-health-${site.site_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h4 className="font-bold text-sm truncate" title={site.name}>{site.name}</h4>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">{site.compliance_status}</div>
                  </div>
                  <span className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${t === "red" ? "bg-red-500 status-pulse-red" : t === "amber" ? "bg-amber-500" : "bg-emerald-500 status-pulse-green"}`} />
                </div>
                <div className="grid grid-cols-3 gap-1.5 mt-2 text-center">
                  <div className="bg-emerald-50 rounded py-1"><div className="text-lg font-bold text-emerald-700 leading-tight">{site.green}</div><div className="text-[9px] uppercase tracking-wider text-emerald-700/70">OK</div></div>
                  <div className="bg-amber-50 rounded py-1"><div className="text-lg font-bold text-amber-700 leading-tight">{site.amber}</div><div className="text-[9px] uppercase tracking-wider text-amber-700/70">Warn</div></div>
                  <div className="bg-red-50 rounded py-1"><div className="text-lg font-bold text-red-700 leading-tight">{site.red}</div><div className="text-[9px] uppercase tracking-wider text-red-700/70">Crit</div></div>
                </div>
                <div className="mt-2 text-[11px] flex items-center justify-between">
                  <span className="text-gray-500">Open alerts</span>
                  <span className={`font-bold ${site.open_alerts ? "text-orange-600" : "text-emerald-600"}`}>{site.open_alerts}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      </>
      )}
    </>
  );
}

function ManagerDashboard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/stats/site-manager").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
  }, []);

  const ack = async (id) => {
    setBusy(id); try { await api.post(`/alerts/${id}/acknowledge`); toast.success("Acknowledged"); load(); } finally { setBusy(null); }
  };

  if (!s?.site) return <div className="glass p-8 rounded-lg text-gray-500">No site assigned to your account yet.</div>;

  const donutData = [
    { name: "Normal", value: s.donut.normal, color: "#22c55e" },
    { name: "Warning", value: s.donut.warning, color: "#f59e0b" },
    { name: "Critical", value: s.donut.critical, color: "#ef4444" },
    { name: "Offline", value: s.donut.offline, color: "#64748b" },
  ].filter((x) => x.value > 0);

  return (
    <>
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl md:text-2xl font-extrabold tracking-tight">{s.site.name}</h1>
        <span className="text-[11px] text-gray-500">Site Manager dashboard · {s.site.address}</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={Activity} label="Total Devices" value={s.total_devices} tone="purple" />
        <KPI icon={Bell} label="Active Alerts" value={s.active_alerts} tone={s.active_alerts ? "amber" : "green"} />
        <KPI icon={Gauge} label="Compliance Score" value={`${s.compliance_score}%`} tone={s.compliance_score >= 80 ? "green" : "red"} />
        {/* Was a hardcoded "Last Test: Today" tile — it always said Today regardless of any real
            test. Replaced with a real figure from the same payload. */}
        <KPI icon={Wifi} label="Offline Devices" value={s.donut.offline} tone={s.donut.offline ? "red" : "green"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="glass p-4 rounded-lg">
          <h3 className="font-semibold mb-2">Device Status Breakdown</h3>
          {donutData.length ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={donutData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value" stroke="none">
                  {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip contentStyle={CHART_TOOLTIP} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="text-gray-500 text-sm py-12 text-center">No device data</div>}
        </div>
        <div className="glass p-4 rounded-lg lg:col-span-2">
          <h3 className="font-semibold mb-2">Recent Alerts</h3>
          <div className="divide-y divide-gray-100">
            {s.recent_alerts?.slice(0, 10).map((a) => (
              <div key={a.alert_id} className="py-2 flex items-start gap-2.5">
                <SeverityDot severity={a.severity} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{a.title}</div>
                  <div className="text-[11px] text-gray-500">{timeAgo(a.created_at)}</div>
                </div>
                {!a.acknowledged && (
                  <Button size="sm" onClick={() => ack(a.alert_id)} disabled={busy === a.alert_id} className="f360-gradient border-0 text-xs">Ack</Button>
                )}
              </div>
            ))}
            {!s.recent_alerts?.length && <div className="text-gray-500 text-sm py-6 text-center">No recent alerts.</div>}
          </div>
        </div>
      </div>
    </>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { alerts, setAlerts, sites, selectedSite } = useSiteContext();

  const siteFilter = selectedSite && selectedSite !== "all" ? selectedSite : null;
  const visibleAlerts = siteFilter ? alerts?.filter((a) => a.site_id === siteFilter) : alerts;
  const criticalAlert = visibleAlerts?.find((a) => !a.acknowledged && a.severity === "critical");

  const ack = async (id) => {
    await api.post(`/alerts/${id}/acknowledge`);
    setAlerts((prev) => prev.map((a) => (a.alert_id === id ? { ...a, acknowledged: true } : a)));
    toast.success("Alert acknowledged");
  };

  const isAdmin = ["admin", "customer_admin", "super_admin"].includes(user?.role);

  return (
    <div className="space-y-3" data-testid="dashboard-page">
      {criticalAlert && (
        <div className="banner-blink rounded-lg border border-red-300 bg-gradient-to-r from-red-50 via-orange-50 to-pink-50 px-4 py-3 flex items-center gap-3" data-testid="critical-banner">
          <AlertTriangle className="w-6 h-6 text-red-600 shrink-0" strokeWidth={2.5} />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-wider text-red-700">Critical</div>
            <div className="text-base text-gray-900 truncate">
              {criticalAlert.title} at {sites.find((s) => s.site_id === criticalAlert.site_id)?.name} — {new Date(criticalAlert.created_at).toLocaleTimeString("en-IN")}
            </div>
          </div>
          <Button onClick={() => ack(criticalAlert.alert_id)} className="f360-gradient border-0 text-white shrink-0" data-testid="ack-critical-btn">
            <span className="hidden sm:inline">Acknowledge</span>
            <span className="sm:hidden">Ack</span>
          </Button>
        </div>
      )}

      {isAdmin ? <AdminDashboard alerts={visibleAlerts} ack={ack} criticalAlert={criticalAlert} /> : <ManagerDashboard />}
    </div>
  );
}
